import {
  newId,
  type ArbiterEvent,
  type BargeinClassifyResult,
  type BargeinOutcome,
  type BargeinTier,
  type EotCheckResult,
  type ServerEvent,
  type Settings,
  type SttResult,
  type TurnMark,
} from '@voicer/shared';
import { ws } from '../ws/client';
import { useStore } from '../state/store';
import { Mic } from '../audio/mic';
import { Player } from '../audio/player';
import { EchoGuard, NoiseFloor } from '../audio/echoGuard';
import { durationMs, sliceAudioAfterWallTime } from '../audio/wav';
import { BargeInArbiter, type InterruptMarks } from '../arbiter/bargeInArbiter';
import { decideWatchdogAction, type WatchdogSnapshot } from './stateWatchdog';
import { arbiterStateAfterEmptyStt, shouldRetryEmptyStt } from './emptySttRecovery';
import { mergeInterruptTranscript } from './transcriptMerge';
import { typedTurnWantsAudio } from './turnAudio';
import { UtteranceCapture } from './utteranceCapture';

interface ActiveTurn {
  turnId: string;
  generating: boolean;
  audioPending: boolean;
  /** True once any audio chunk for this turn has started playing. */
  audioStarted: boolean;
}

interface PendingUtterance {
  text: string;
  marks: Partial<Record<TurnMark, number>>;
  extendTimer: number | null;
  waitingForContinuation: boolean;
  /** Delay last requested by EoT (grace vs extend); used when re-arming after a hold. */
  commitDelayMs: number;
  /** Continuation recall: id of the already-stored user message to extend instead of inserting. */
  replaceUserMessageId?: string;
}

interface LastCommit {
  turnId: string;
  text: string;
  marks: Partial<Record<TurnMark, number>>;
  ts: number;
}

/**
 * One user utterance that interrupted the assistant. Owns the audio from onset until commit.
 * `pending` (normal endpointing) is never used while this is open — mixing the two state
 * machines is what produced duplicated turns.
 */
interface InterruptTurn {
  capture: UtteranceCapture;
  speechStart: number;
  /** capturing → (silence) transcribing → grace → commit; speech during grace reopens capturing. */
  phase: 'capturing' | 'transcribing' | 'grace';
  graceTimer: number | null;
  /** Transcript awaiting commit (flushed text + buffered audio); display-only until commit. */
  finalText: string;
  /** Lexical/Tier-B transcript from barge-in STT; used if the interrupt re-STT comes back empty. */
  seedTranscript: string;
  /** Serializes flush + endpoint STT so flushed text stays in order. */
  chain: Promise<void>;
  /** Bumped on reopen so a stale STT pass cannot commit. */
  gen: number;
  emptyPasses: number;
}

/**
 * Client-side conversation orchestrator: mic + VAD -> endpointing -> STT -> turn; server events -> player;
 * barge-in arbitration while the assistant is active.
 */
export class ConversationController {
  mic: Mic;
  player: Player;
  echoGuard = new EchoGuard(16);
  noiseFloor = new NoiseFloor();
  arbiter: BargeInArbiter;
  private active: ActiveTurn | null = null;
  private pending: PendingUtterance | null = null;
  /** VAD-level flag: the user is speaking right now (independent of STT progress). */
  private userSpeaking = false;
  private lastSpeechEndTs = 0;
  /** Segments transcribing on the main lane; commits wait for them. */
  private sttInflight = 0;
  /** Most recent committed turn, for continuation recall. */
  private lastCommit: LastCommit | null = null;
  /** Turns that were interrupted; late audio chunks for them are dropped. */
  private deadTurns = new Set<string>();
  private telemetryTick = 0;
  private started = false;
  private lastSettings: Settings | null = null;
  /** Audio chunks still decoding; prevents idle/bias drop between quote clips. */
  private audioInflight = 0;
  /** Timer holding raised VAD thresholds across a cue tone so the mic ignores it. */
  private cueBiasTimer: number | null = null;
  /** Mid-utterance streaming STT timer. */
  private partialSttTimer: number | null = null;
  private partialSttInflight = false;
  private streamRaf: number | null = null;
  private streamPending: { turnId: string; text: string } | null = null;
  /** Clears stuck userSpeaking when VAD never ends (muted mic / phantom onset). */
  private speechWatchdog: number | null = null;
  private static readonly SPEECH_WATCHDOG_MS = 8_000;
  /** Periodic wait-state watchdog (see stateWatchdog.ts); no wait state may hang indefinitely. */
  private stateWatchdogTimer: number | null = null;
  private static readonly STATE_WATCHDOG_TICK_MS = 1_000;
  private lastSeenState: string | null = null;
  private lastSeenStateTs = Date.now();
  /** eot.check RPCs in flight (pending has no commit timer while one runs). */
  private eotInflight = 0;
  /**
   * Continuous mic frames from a barge-in onset. Survives premature VAD speechEnd when TTS
   * pause/stop fools Silero. Becomes the InterruptTurn's audio when the barge-in is confirmed.
   */
  private capture: UtteranceCapture | null = null;
  private interrupt: InterruptTurn | null = null;
  /** Wall time of the last interrupt commit; VAD segments that started earlier are trimmed to it. */
  private committedUntilTs = 0;
  /** Energy silence (not Silero) that ends an interrupt utterance. */
  private static readonly INTERRUPT_SILENCE_MS = 650;
  /** Silence ignored right after the assistant is stopped (echo decay / AEC re-adapt). */
  private static readonly INTERRUPT_HOLD_MS = 400;
  /** Hard cap on one interrupt utterance; the state watchdog normally endpoints far earlier. */
  private static readonly INTERRUPT_MAX_MS = 20_000;
  /** Stop/supersede must not wait on a slow server; local state is already cleared. */
  private static readonly INTERRUPT_RPC_TIMEOUT_MS = 5_000;

  constructor() {
    this.player = new Player({
      onChunkStart: (turnId, index) => {
        void ws.rpc('audio.mark', { turnId, index, mark: 'started', ts: Date.now() }).catch(() => {});
        if (this.active?.turnId === turnId) {
          this.active.audioPending = true;
          this.active.audioStarted = true;
        }
        if (!this.arbiter.active) useStore.getState().setArbiterState('speaking');
        this.mic.setPlaybackBias(true, this.settings);
      },
      onChunkEnd: (turnId, index) => {
        void ws.rpc('audio.mark', { turnId, index, mark: 'ended', ts: Date.now() }).catch(() => {});
      },
      onIdle: (turnId) => {
        this.onPlayerIdle(turnId);
      },
    });
    this.mic = new Mic({
      onSpeechStart: (ts) => this.onSpeechStart(ts),
      onSpeechEnd: (audio, s, e) => void this.onSpeechEnd(audio, s, e),
      onMisfire: () => this.onVadMisfire(),
      onFrame: (prob, micRms, frame) => this.onFrame(prob, micRms, frame),
    });
    this.arbiter = new BargeInArbiter({
      settings: () => this.settings,
      player: this.player,
      echoGuard: this.echoGuard,
      activeTurnId: () => this.active?.turnId ?? this.player.activeTurn(),
      // Include paused playback so classify/STT still see an active assistant turn.
      assistantActive: () => this.assistantActive() || this.player.paused,
      noiseFloor: () => this.noiseFloor.value,
      stt: (audio) => this.sttBargeIn(audio),
      classify: (turnId, transcript, currentSentence) => ws.rpc<BargeinClassifyResult>('bargein.classify', { turnId, transcript, currentSentence }, 20_000),
      hardInterrupt: (turnId, tier, transcript, marks) => this.hardInterrupt(turnId, tier, transcript, marks),
      softYield: (turnId, tier, transcript, marks) => this.softYield(turnId, tier, transcript, marks),
      aside: (turnId, transcript, outcome, currentSentence) => this.addAside(turnId, transcript, outcome, currentSentence),
      handoffToMain: (transcript, marks) => void this.handleTranscript(transcript, { speechStart: marks.speechStart, speechEnd: marks.speechEnd, sttDone: Date.now() }),
      recordEvent: (ev) => this.recordEvent(ev),
      setState: (s) => useStore.getState().setArbiterState(s),
      setPlaybackBias: (active) => this.mic.setPlaybackBias(active, this.settings),
      clearCapture: () => this.clearCapture(),
      log: (msg) => useStore.getState().log('info', `[arbiter] ${msg}`),
    });
    ws.on((ev) => this.onServerEvent(ev));
    // Text-only users never call start(); still need hung-turn recovery.
    this.startStateWatchdog();
  }

  get settings(): Settings {
    return useStore.getState().settings;
  }

  assistantActive(): boolean {
    return this.player.isPlaying() || (this.active !== null && (this.active.generating || this.active.audioPending));
  }

  /** True while a turn is in-flight on the client (used after reconnect). */
  hasActiveTurn(): boolean {
    return this.active !== null;
  }

  /**
   * Clear local turn wait-state after disconnect, reconnect, or watchdog.
   * Does not call the server (turn may already be dead).
   */
  resetTurnState(reason: string): void {
    if (this.active) this.markDead(this.active.turnId);
    this.active = null;
    this.lastCommit = null;
    this.clearStreamPending();
    useStore.getState().setStreaming(null);
    useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
    useStore.getState().log('warn', `turn state reset (${reason})`);
  }

  private clearStreamPending(): void {
    this.streamPending = null;
    if (this.streamRaf !== null) {
      cancelAnimationFrame(this.streamRaf);
      this.streamRaf = null;
    }
  }

  // ---- lifecycle -------------------------------------------------------------

  /** Must be called from a user gesture (AudioContext + getUserMedia). */
  async start() {
    if (this.started) return;
    this.started = true;
    await this.player.resume();
    useStore.getState().setAudio({ baseLatencyMs: this.player.baseLatencyMs, outputLatencyMs: this.player.outputLatencyMs });
    try {
      await this.mic.init(this.settings);
      useStore.getState().setAudio({ micReady: true, micError: null });
    } catch (err) {
      useStore.getState().setAudio({ micReady: false, micError: err instanceof Error ? err.message : String(err) });
      useStore.getState().log('error', `microphone init failed: ${err instanceof Error ? err.message : err}`);
    }
    await this.applyMode();
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    this.startStateWatchdog();
  }

  async applySettings(s: Settings) {
    const prev = this.lastSettings;
    this.lastSettings = s;
    this.player.setDuckLevel(s.duckLevel);
    if (this.mic.ready) this.mic.applySettings(s);
    if (!prev || prev.mode !== s.mode) await this.applyMode();
  }

  private async applyMode() {
    const mode = this.settings.mode;
    if (!this.mic.ready) return;
    await this.mic.setMode(mode === 'text' ? 'off' : mode);
    if (mode !== 'duplex') {
      // Silero pause never emits speechEnd/misfire: an open onset would leave userSpeaking stuck.
      this.userSpeaking = false;
      this.clearSpeechWatchdog();
      this.clearPartialStt();
      if (this.arbiter.active) this.arbiter.cancelShort();
      this.rearmCommit();
    }
    useStore.getState().setArbiterState(mode === 'text' ? 'idle' : this.assistantActive() ? 'speaking' : 'listening');
  }

  // ---- state watchdog -------------------------------------------------------------

  private startStateWatchdog() {
    if (this.stateWatchdogTimer !== null) return;
    this.stateWatchdogTimer = window.setInterval(() => this.stateWatchdogTick(), ConversationController.STATE_WATCHDOG_TICK_MS);
  }

  private watchdogSnapshot(now: number): WatchdogSnapshot {
    const state = useStore.getState().arbiterState;
    if (state !== this.lastSeenState) {
      this.lastSeenState = state;
      this.lastSeenStateTs = now;
    }
    const it = this.interrupt;
    return {
      arbiterState: state,
      stateSinceTs: this.lastSeenStateTs,
      hasPending: this.pending !== null,
      pendingTimerArmed: this.pending?.extendTimer !== null && this.pending?.extendTimer !== undefined,
      userSpeaking: this.userSpeaking,
      sttInflight: this.sttInflight,
      eotInflight: this.eotInflight,
      hasActiveTurn: this.active !== null,
      arbiterActive: this.arbiter.active,
      interruptPhase: it?.phase ?? null,
      interruptLastEnergeticTs: it ? it.capture.lastEnergeticTs : null,
      interruptEndpointArmed: it?.capture.endpointingArmed ?? false,
    };
  }

  private stateWatchdogTick() {
    const now = Date.now();
    const action = decideWatchdogAction(this.watchdogSnapshot(now), now);
    if (action === 'none') return;
    useStore.getState().log('warn', `state watchdog: ${action}`);
    switch (action) {
      case 'force-endpoint':
        if (this.interrupt) void this.endpointInterrupt(this.interrupt, false);
        break;
      case 'rearm-commit':
        this.rearmCommit();
        break;
      case 'to-listening':
        useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
        break;
      case 'force-clear-turn':
        this.resetTurnState('watchdog');
        break;
    }
    this.lastSeenStateTs = now;
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code !== 'Space' || this.settings.mode !== 'ptt') return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    e.preventDefault();
    this.pttDown();
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code !== 'Space' || this.settings.mode !== 'ptt') return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    e.preventDefault();
    this.pttUp();
  };

  pttDown() {
    if (this.assistantActive()) void this.stopAssistant('duration');
    this.mic.pttDown();
    useStore.getState().setAudio({ pttHeld: true });
  }
  pttUp() {
    this.mic.pttUp();
    useStore.getState().setAudio({ pttHeld: false });
  }

  // ---- mic events ------------------------------------------------------------

  private onFrame(prob: number, micRms: number, frame?: Float32Array) {
    const outRms = this.player.isPlaying() ? this.player.outputRms() : 0;
    this.echoGuard.push(micRms, outRms);
    // Energy evidence: Silero often drops to "silence" the moment TTS stops even though the user
    // is mid-sentence, so the interrupt utterance is endpointed on mic energy OR VAD.
    const floor = Math.max(this.noiseFloor.value, 0.002);
    // Interrupt capture: VAD-only energy. Mic-RMS ambient noise was preventing silence
    // endpoints and leaving the UI stuck on Listening after barge-in.
    const energetic = this.interrupt
      ? prob >= this.settings.vadPositiveThreshold
      : prob >= this.settings.vadPositiveThreshold || micRms >= Math.max(floor * 2.5, 0.004);
    // Do not let the user's own speech — or the assistant's bleed while it is audible — drag the
    // noise floor up (a raised floor defeats the arbiter's syllable-peak sustained-speech test).
    this.noiseFloor.update(micRms, prob > 0.5 || (this.capture !== null && energetic) || this.player.isAudible());
    if (this.capture && frame?.length) {
      const tick = this.capture.push(new Float32Array(frame), energetic, micRms, Date.now());
      if (this.interrupt) this.onInterruptTick(tick);
    }
    this.arbiter.onFrame(prob, micRms);
    if (++this.telemetryTick % 3 === 0) {
      useStore.getState().setAudio({
        micLevel: micRms,
        vadProb: prob,
        noiseFloor: this.noiseFloor.value,
        echoCorr: this.echoGuard.lastCorrelation,
        echoChronic: this.echoGuard.chronic,
        pttHeld: this.mic.pttHeld,
      });
    }
  }

  private onSpeechStart(ts: number) {
    this.userSpeaking = true;
    // Interrupt utterance open: the capture owns endpointing. Speech during grace cancels commit.
    if (this.interrupt) {
      if (this.interrupt.phase === 'grace') this.reopenInterrupt('vad-start');
      useStore.getState().setArbiterState('listening');
      return;
    }
    this.armSpeechWatchdog();
    // Continuation recall: the user kept talking right after we committed, and no reply audio has
    // played yet. Pull the turn back and merge the new speech into it instead of interrupting.
    if (this.tryRecallLastCommit(ts)) return;
    // Barge-in arbitration is for playback. While the assistant is only thinking (no audio yet),
    // more speech is simply more of the user's turn.
    if (this.assistantAudible() && this.arbiter.onSpeechStart(ts)) {
      // Keep one open capture across premature VAD restarts during classify.
      this.capture ??= this.newCapture(ts, 450);
      return;
    }
    // User continued speaking during a grace / extension window: hold the pending utterance.
    this.holdPending();
    if (!this.pending) useStore.getState().setArbiterState('listening');
    this.armPartialStt();
  }

  // ---- barge-in capture / interrupt utterance ----------------------------------

  private newCapture(startTs: number, leadMs: number): UtteranceCapture {
    // Ring lead-in so the syllables before the first captured frame are kept.
    const lead = this.mic.ringSnapshot(leadMs);
    return new UtteranceCapture(startTs, lead.length ? lead : null, {
      silenceMs: ConversationController.INTERRUPT_SILENCE_MS,
      holdMs: ConversationController.INTERRUPT_HOLD_MS,
      maxMs: ConversationController.INTERRUPT_MAX_MS,
    });
  }

  /** Drop a barge-in capture that did not become an interrupt (backchannel / noise / echo). */
  private clearCapture() {
    if (this.interrupt) return; // the interrupt utterance owns it now
    this.capture = null;
  }

  private clearInterrupt() {
    const it = this.interrupt;
    if (it?.graceTimer) window.clearTimeout(it.graceTimer);
    if (it) it.gen++;
    this.interrupt = null;
    this.capture = null;
  }

  private async sttBargeIn(vadAudio: Float32Array): Promise<SttResult> {
    const captured = this.capture?.audio();
    const audio =
      captured && captured.length > Math.max(vadAudio.length, 16000 * 0.25) ? captured : vadAudio;
    return this.stt(audio, 'bargein');
  }

  /**
   * The assistant has been stopped for this user's speech. From here the utterance is one
   * continuous capture: no `pending`, no VAD-segment STT, one commit.
   */
  private beginInterruptUtterance(seedTranscript: string | null, speechStart: number, cleanFrom?: number) {
    // Already claimed (sync open before turn.interrupt await) — keep capture, refresh seed/display.
    if (this.interrupt) {
      const seed = (seedTranscript ?? '').trim();
      if (seed) this.interrupt.seedTranscript = seed;
      useStore.getState().setPendingUtterance(seed ? `${seed} …` : '…listening');
      useStore.getState().setArbiterState('listening');
      return;
    }
    // Any normal pending fragment is superseded by the interrupt utterance.
    if (this.pending?.extendTimer) window.clearTimeout(this.pending.extendTimer);
    this.pending = null;
    this.capture ??= this.newCapture(speechStart, 1500);
    // Drop assistant bleed recorded before the clean cut so the re-STT never contains its words.
    this.capture.dropBefore(cleanFrom ?? speechStart);
    const now = Date.now();
    this.capture.armEndpointing(now);
    const seed = (seedTranscript ?? '').trim();
    this.interrupt = {
      capture: this.capture,
      speechStart,
      phase: 'capturing',
      graceTimer: null,
      finalText: '',
      seedTranscript: seed,
      chain: Promise.resolve(),
      gen: 0,
      emptyPasses: 0,
    };
    this.committedUntilTs = 0;
    this.clearSpeechWatchdog();
    this.clearPartialStt();
    // Playback bias blocks soft speech; drop it so the rest of the interrupt is heard.
    this.mic.setPlaybackBias(false, this.settings);
    useStore.getState().setPendingUtterance(seed ? `${seed} …` : '…listening');
    useStore.getState().setArbiterState('listening');
  }

  private onInterruptTick(tick: 'speech' | 'silence' | 'endpoint' | 'max') {
    const it = this.interrupt;
    if (!it) return;
    if (tick === 'speech') {
      if (it.phase === 'grace') this.reopenInterrupt('energy');
      return;
    }
    if (it.phase !== 'capturing') return;
    if (it.capture.needsFlush()) this.flushInterruptChunk(it);
    if (tick === 'endpoint') void this.endpointInterrupt(it, false);
    else if (tick === 'max') void this.endpointInterrupt(it, true);
  }

  /** Long monologue: STT the oldest audio once and drop it, so the buffer never overlaps itself. */
  private flushInterruptChunk(it: InterruptTurn) {
    const chunk = it.capture.takeFlushChunk();
    if (!chunk) return;
    it.chain = it.chain.then(async () => {
      try {
        const r = await this.stt(chunk, 'main', it.capture.flushedText || undefined);
        it.capture.addFlushedText(r.text);
      } catch (err) {
        useStore.getState().log('warn', `interrupt flush STT failed: ${err instanceof Error ? err.message : err}`);
      }
    });
  }

  /** Speech resumed during commit grace: cancel the commit; audio is still buffered. */
  private reopenInterrupt(reason: string) {
    const it = this.interrupt;
    if (it?.phase !== 'grace') return;
    if (it.graceTimer) window.clearTimeout(it.graceTimer);
    it.graceTimer = null;
    it.gen++;
    it.phase = 'capturing';
    const shown = it.finalText.trim();
    it.finalText = '';
    useStore.getState().setPendingUtterance(shown ? `${shown} …` : '…listening');
    useStore.getState().setArbiterState('listening');
    useStore.getState().log('info', `[arbiter] interrupt utterance continues (${reason}); commit cancelled`);
  }

  /**
   * Energy silence reached: transcribe the buffered audio once. If more speech arrived while
   * Whisper ran, discard this pass (the audio is still buffered and will be transcribed again as
   * part of the whole). Otherwise run EoT and arm a grace timer that speech can cancel.
   */
  private async endpointInterrupt(it: InterruptTurn, force: boolean) {
    if (this.interrupt !== it || it.phase !== 'capturing') return;
    it.phase = 'transcribing';
    const gen = ++it.gen;
    const mark = it.capture.totalSamples;
    const audio = it.capture.audio();
    useStore.getState().setArbiterState('thinking');
    it.chain = it.chain.then(async () => {
      const heard = await this.transcribeInterrupt(it, audio);
      if (this.interrupt !== it || it.gen !== gen || it.phase !== 'transcribing') return;
      if (!force && it.capture.grewSince(mark)) {
        // User kept talking — this transcript covers only part of the utterance. Drop it.
        this.continueInterrupt(it, heard ? `${heard} …` : null);
        return;
      }
      if (!heard) {
        this.onEmptyInterruptPass(it, force);
        return;
      }
      const text = mergeInterruptTranscript(it.seedTranscript, heard);
      it.finalText = text;
      useStore.getState().setPendingUtterance(text);
      if (force) {
        this.commitInterrupt(it);
        return;
      }
      await this.armInterruptGrace(it, gen, text);
    });
  }

  /** One STT of the buffered audio, prefixed with any flushed text. Never merges overlapping text. */
  private async transcribeInterrupt(it: InterruptTurn, audio: Float32Array): Promise<string> {
    if (audio.length < 16000 * 0.25) return it.capture.flushedText.trim();
    try {
      const r = await this.stt(audio, 'main', it.capture.flushedText || undefined);
      return it.capture.fullText(r.text).trim();
    } catch (err) {
      useStore.getState().log('warn', `interrupt STT failed: ${err instanceof Error ? err.message : err}`);
      return it.capture.flushedText.trim();
    }
  }

  private continueInterrupt(it: InterruptTurn, display: string | null) {
    it.phase = 'capturing';
    if (display) useStore.getState().setPendingUtterance(display);
    useStore.getState().setArbiterState('listening');
  }

  private onEmptyInterruptPass(it: InterruptTurn, force: boolean) {
    it.emptyPasses++;
    const seed = it.seedTranscript.trim();
    // Chirp often blanks the re-STT; commit the barge-in lexical transcript if we have one.
    if (seed && (force || it.emptyPasses >= 1)) {
      useStore.getState().log('info', '[arbiter] interrupt re-STT empty; committing barge-in seed');
      it.finalText = seed;
      this.commitInterrupt(it);
      return;
    }
    if (force || it.emptyPasses >= 2) {
      useStore.getState().log('info', '[arbiter] interrupt utterance had no speech; dropping');
      this.clearInterrupt();
      useStore.getState().setPendingUtterance('');
      useStore.getState().setArbiterState('listening');
      return;
    }
    it.capture.armEndpointing(Date.now());
    this.continueInterrupt(it, null);
  }

  /** EoT-aware grace measured from the last confirmed speech; speech during grace cancels it. */
  private async armInterruptGrace(it: InterruptTurn, gen: number, text: string) {
    it.phase = 'grace';
    void ws.rpc('turn.prepare', { text }).catch(() => {});
    let extra = this.settings.commitGraceMs;
    this.eotInflight++;
    try {
      const eot = await ws.rpc<EotCheckResult>('eot.check', { text }, 8_000);
      if (eot.confidence === 'high' && eot.complete) extra = this.settings.commitGraceHighMs ?? 180;
      else if (!eot.complete) extra = this.settings.eotExtendMs;
    } catch {
      /* default grace */
    } finally {
      this.eotInflight--;
    }
    if (this.interrupt !== it || it.gen !== gen || it.phase !== 'grace') return;
    const target = ConversationController.INTERRUPT_SILENCE_MS + extra;
    const wait = Math.max(0, target - (Date.now() - it.capture.lastEnergeticTs));
    it.graceTimer = window.setTimeout(() => {
      it.graceTimer = null;
      if (this.interrupt === it && it.phase === 'grace') this.commitInterrupt(it);
    }, wait);
  }

  private commitInterrupt(it: InterruptTurn) {
    if (this.interrupt !== it) return;
    const text = it.finalText.trim();
    const marks: Partial<Record<TurnMark, number>> = {
      speechStart: it.speechStart,
      speechEnd: it.capture.lastEnergeticTs,
      sttDone: Date.now(),
    };
    // Any VAD segment that began before this instant overlaps committed audio; onSpeechEnd trims it.
    this.committedUntilTs = Date.now();
    this.clearInterrupt();
    useStore.getState().setPendingUtterance('');
    if (!text) {
      useStore.getState().setArbiterState('listening');
      return;
    }
    this.playTurnCue();
    void this.startTurn(text, marks, this.settings.ttsEnabled);
  }

  private armSpeechWatchdog() {
    if (this.speechWatchdog) window.clearTimeout(this.speechWatchdog);
    this.speechWatchdog = window.setTimeout(() => {
      this.speechWatchdog = null;
      if (this.interrupt) return; // capture maxMs covers this case
      if (!this.userSpeaking) return;
      useStore.getState().log('warn', 'speech watchdog: clearing stuck listening (no VAD end)');
      this.userSpeaking = false;
      this.clearPartialStt();
      if (this.arbiter.active) this.arbiter.cancelShort();
      this.rearmCommit();
      if (!this.assistantAudible() && !this.pending) {
        useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
      } else if (this.assistantAudible() && !this.arbiter.active) {
        useStore.getState().setArbiterState('speaking');
      }
    }, ConversationController.SPEECH_WATCHDOG_MS);
  }

  private clearSpeechWatchdog() {
    if (this.speechWatchdog) {
      window.clearTimeout(this.speechWatchdog);
      this.speechWatchdog = null;
    }
  }

  /** Periodically STT the ring buffer during duplex speech for earlier prepare/EoT. */
  private armPartialStt() {
    if (!this.settings.streamingStt || this.settings.mode !== 'duplex') return;
    if (this.partialSttTimer) return;
    this.partialSttTimer = window.setInterval(() => void this.runPartialStt(), 700);
  }

  private clearPartialStt() {
    if (this.partialSttTimer) {
      window.clearInterval(this.partialSttTimer);
      this.partialSttTimer = null;
    }
  }

  private async runPartialStt() {
    if (!this.userSpeaking || this.partialSttInflight || this.assistantAudible()) return;
    const audio = this.mic.ringSnapshot(2500);
    if (!audio || audio.length < 16000 * 0.4) return;
    this.partialSttInflight = true;
    try {
      const r = await this.stt(audio, 'main', this.pending?.text, true);
      if (r.text && this.userSpeaking) {
        useStore.getState().setPendingUtterance(r.text);
        void ws.rpc('turn.prepare', { text: r.text }).catch(() => {});
      }
    } catch {
      /* partials are best-effort */
    } finally {
      this.partialSttInflight = false;
    }
  }

  /**
   * Silero fires speechStart then misfire when the burst is shorter than minSpeechMs.
   * Without this, userSpeaking stays true forever and armCommit never schedules a timer.
   */
  private onVadMisfire() {
    // Interrupt utterance open: VAD blips are irrelevant; the capture endpoints on energy.
    if (this.interrupt) return;
    if (!this.userSpeaking) return;
    this.userSpeaking = false;
    this.clearSpeechWatchdog();
    this.clearPartialStt();
    this.lastSpeechEndTs = Date.now();
    // A barge-in candidate that never produced a segment must unduck / unpause.
    if (this.arbiter.active) this.arbiter.cancelShort();
    if (!this.arbiter.active) this.clearCapture();
    this.rearmCommit();
  }

  /** True when the assistant's voice is (or is about to be) audible: playing, paused, or chunks queued. */
  private assistantAudible(): boolean {
    return this.player.isPlaying() || this.player.paused || (this.active?.audioStarted ?? false);
  }

  private holdPending() {
    if (!this.pending) return;
    if (this.pending.extendTimer) {
      window.clearTimeout(this.pending.extendTimer);
      this.pending.extendTimer = null;
    }
    this.pending.waitingForContinuation = true;
  }

  private tryRecallLastCommit(onsetTs: number): boolean {
    const lc = this.lastCommit;
    if (!lc) return false;
    const s = this.settings;
    if (onsetTs - lc.ts > s.continuationRecallMs) return false;
    const a = this.active;
    if (!a || a.turnId !== lc.turnId) return false;
    if (a.audioStarted || this.player.isPlaying() || this.player.paused) return false;
    // Abort the turn server-side (deletes the empty assistant placeholder), keep the user message
    // so the merged text can replace it.
    this.markDead(lc.turnId);
    this.arbiter.cancel({ resume: false });
    void ws.rpc('turn.interrupt', { turnId: lc.turnId, heardText: '', reason: 'recall' }).catch(() => {});
    this.active = null;
    this.lastCommit = null;
    useStore.getState().setStreaming(null);
    const userMsg = useStore
      .getState()
      .session?.messages.find((m) => m.turnId === lc.turnId && m.role === 'user');
    this.pending = {
      text: lc.text,
      marks: { ...lc.marks },
      extendTimer: null,
      waitingForContinuation: true,
      commitDelayMs: s.commitGraceMs,
      replaceUserMessageId: userMsg?.id,
    };
    useStore.getState().setPendingUtterance(lc.text);
    useStore.getState().setArbiterState('listening');
    useStore.getState().log('info', `recalled turn ${lc.turnId} (user kept talking ${onsetTs - lc.ts} ms after commit)`);
    return true;
  }

  private async onSpeechEnd(audio: Float32Array, startTs: number, endTs: number) {
    this.userSpeaking = false;
    this.clearSpeechWatchdog();
    this.clearPartialStt();
    this.lastSpeechEndTs = endTs;
    // Interrupt utterance open: this audio is already in the capture; Silero ends are ignored.
    if (this.interrupt) return;
    if (this.arbiter.active) {
      await this.onBargeSegmentEnd(audio, startTs, endTs);
      return;
    }
    // A segment that began before the last interrupt commit overlaps audio already sent to the
    // agent. Keep only what was recorded after the commit instant (never re-transcribe audio).
    const uncommitted = this.uncommittedPart(audio, startTs, endTs);
    if (!uncommitted) {
      useStore.getState().log('info', 'dropping VAD tail already covered by the interrupt commit');
      if (!this.pending) useStore.getState().setArbiterState(this.assistantActive() ? 'thinking' : 'listening');
      this.rearmCommit();
      return;
    }
    await this.processSegment(uncommitted.audio, uncommitted.startTs, endTs);
  }

  private async onBargeSegmentEnd(audio: Float32Array, startTs: number, endTs: number) {
    // A second VAD segment while the first is still being classified: the arbiter keeps it so a
    // resume decision can be escalated (the user kept talking → they want the floor).
    if (this.arbiter.isDeciding) {
      this.arbiter.queueSegmentWhileDeciding(audio, startTs, endTs);
      return;
    }
    const consumed = await this.arbiter.onSpeechEnd(audio, startTs, endTs);
    // hardInterrupt / softYield open the interrupt utterance synchronously; it owns capture.
    // Do not clearCapture on the consumed path — that raced with async turn.interrupt and
    // wiped the mic buffer before beginInterruptUtterance could claim it.
    if (this.interrupt || consumed) {
      this.rearmCommit();
      return;
    }
    // Defensive: not consumed and no interrupt opened — treat as a normal segment.
    const cleaned = this.arbiter.takeMainLaneAudio();
    this.clearCapture();
    await this.processSegment(cleaned ?? audio, startTs, endTs);
  }

  /** Audio after the last interrupt commit, or null when too little remains. */
  private uncommittedPart(audio: Float32Array, startTs: number, endTs: number): { audio: Float32Array; startTs: number } | null {
    if (!this.committedUntilTs || startTs >= this.committedUntilTs) return { audio, startTs };
    const seg = sliceAudioAfterWallTime(audio, startTs, endTs, this.committedUntilTs);
    if (durationMs(seg) < 300) return null;
    return { audio: seg, startTs: this.committedUntilTs };
  }

  private async processSegment(audio: Float32Array, startTs: number, endTs: number) {
    const marks: Partial<Record<TurnMark, number>> = { speechStart: startTs, speechEnd: endTs, sttQueued: Date.now() };
    useStore.getState().setArbiterState('thinking');
    const prompt = this.pending?.text;
    const segMs = durationMs(audio);
    let r: SttResult | null = null;
    this.sttInflight++;
    try {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          // Context-prime Whisper with what the user has said so far in this utterance.
          r = await this.stt(audio, 'main', prompt);
        } catch (err) {
          useStore.getState().log('error', `STT failed: ${err instanceof Error ? err.message : err}`);
          this.finishEmptyStt(segMs);
          return;
        }
        if (r.text) break;
        if (shouldRetryEmptyStt(segMs, attempt)) {
          useStore.getState().log('info', `STT returned nothing for a ${Math.round(segMs)} ms segment; retrying once`);
          continue;
        }
        this.finishEmptyStt(segMs);
        return;
      }
    } finally {
      this.sttInflight--;
    }
    if (!r?.text) {
      this.finishEmptyStt(segMs);
      return;
    }
    const now = Date.now();
    marks.sttDone = now;
    marks.sttSent = now - r.sttMs;
    await this.handleTranscript(r.text, marks);
  }

  /** Leave thinking after empty/failed STT; restore listening or speaking and re-arm commit. */
  private finishEmptyStt(segMs: number): void {
    useStore.getState().log('warn', `STT returned nothing for a ${Math.round(segMs)} ms segment`);
    useStore.getState().setArbiterState(arbiterStateAfterEmptyStt(this.assistantAudible()));
    this.rearmCommit();
  }

  /** Adaptive endpointing: merge with a pending fragment, run the end-of-turn check, then arm a grace timer. */
  private async handleTranscript(text: string, marks: Partial<Record<TurnMark, number>>) {
    const incoming = text.trim();
    if (!incoming) return;
    if (this.pending) {
      // Segments are non-overlapping audio, so plain concatenation is correct here.
      this.pending.text = `${this.pending.text} ${incoming}`.trim();
      this.pending.marks.speechEnd = marks.speechEnd ?? Date.now();
      this.pending.marks.sttDone = marks.sttDone;
      if (!this.pending.marks.speechStart) this.pending.marks.speechStart = marks.speechStart;
    } else {
      this.pending = {
        text: incoming,
        marks: { ...marks },
        extendTimer: null,
        waitingForContinuation: false,
        commitDelayMs: this.settings.commitGraceMs,
      };
    }
    this.holdPending();
    useStore.getState().setPendingUtterance(this.pending.text);

    const s = this.settings;
    let graceMs = s.commitGraceMs;
    let speculative = false;
    if (s.mode === 'duplex') {
      const p = this.pending;
      const textAtCheck = p.text;
      // Speculative compile while we wait for EoT / grace (hot path for turn.start).
      void ws.rpc('turn.prepare', { text: textAtCheck }).catch(() => {});
      p.marks.eotCheckStart = Date.now();
      this.eotInflight++;
      try {
        const eot = await ws.rpc<EotCheckResult>('eot.check', { text: textAtCheck }, 8_000);
        if (eot.confidence === 'high') {
          graceMs = s.commitGraceHighMs ?? 180;
          speculative = eot.complete;
        } else if (eot.complete) {
          graceMs = s.commitGraceMs;
        } else {
          graceMs = s.eotExtendMs;
        }
        if (eot.tier === 'llm') {
          useStore.getState().log(
            'info',
            `EoT check (${eot.tier}/${eot.confidence}) -> ${eot.complete ? 'COMPLETE' : 'INCOMPLETE'} in ${eot.ms} ms`,
          );
        }
      } catch {
        graceMs = s.commitGraceMs;
      } finally {
        this.eotInflight--;
      }
      p.marks.eotCheckDone = Date.now();
      if (this.pending !== p) return; // recalled / committed elsewhere meanwhile
      // A newer fragment merged during the check; that handler will arm its own timer.
      if (p.text !== textAtCheck) return;
      // Speculative: high-confidence COMPLETE starts the reply immediately; continuation recall covers extend.
      if (speculative) {
        this.commitPending();
        return;
      }
    }
    if (!this.pending) return;
    if (s.mode !== 'duplex') {
      this.commitPending();
      return;
    }
    this.armCommit(graceMs);
  }

  /**
   * Arm the commit timer measured from the last *transcribed* speech end (pending.marks.speechEnd).
   * Noise / empty VAD blips update lastSpeechEndTs but must not push the grace window out.
   * Never commits while the user is still speaking or another segment is being transcribed.
   */
  private armCommit(delayFromSpeechEnd: number) {
    const p = this.pending;
    if (!p) return;
    p.commitDelayMs = delayFromSpeechEnd;
    if (p.extendTimer) window.clearTimeout(p.extendTimer);
    p.extendTimer = null;
    p.waitingForContinuation = true;
    if (this.userSpeaking || this.sttInflight > 0) {
      // No timer yet — onVadMisfire / onSpeechEnd / processSegment will rearmCommit.
      return;
    }
    const speechEnd = p.marks.speechEnd ?? this.lastSpeechEndTs ?? Date.now();
    const elapsed = Math.max(0, Date.now() - speechEnd);
    const wait = Math.max(0, delayFromSpeechEnd - elapsed);
    p.extendTimer = window.setTimeout(() => {
      if (this.pending !== p) return;
      p.extendTimer = null;
      if (this.userSpeaking || this.sttInflight > 0) {
        // Hold without a timer; the clear-path must rearm.
        return;
      }
      this.commitPending();
    }, wait);
  }

  /** A segment finished without new text (or a speech hold cleared); restart the grace timer if needed. */
  private rearmCommit() {
    const p = this.pending;
    if (!p) return;
    if (this.userSpeaking || this.sttInflight > 0) return;
    if (p.extendTimer) return;
    this.armCommit(p.commitDelayMs || this.settings.commitGraceMs);
  }

  private commitPending() {
    const p = this.pending;
    if (!p) return;
    if (p.extendTimer) window.clearTimeout(p.extendTimer);
    this.pending = null;
    useStore.getState().setPendingUtterance('');
    // Same earcon as end-of-speech: listening finished, agent is processing.
    this.playTurnCue();
    void this.startTurn(p.text, p.marks, this.settings.ttsEnabled, p.replaceUserMessageId);
  }

  private onPlayerIdle(turnId: string) {
    // A dead (interrupted) turn's late decode must not re-bias the mic or chime over the user;
    // while an interrupt utterance is open the capture owns state transitions.
    if (this.deadTurns.has(turnId) || this.interrupt) return;
    // More clips may still be decoding (common with quotes-only: short first quote ends
    // before the next wav finishes decodeAudioData). Stay "speaking" until they enqueue.
    if (this.audioInflight > 0) return;
    let finishedSpeaking = false;
    if (this.active?.turnId === turnId) {
      this.active.audioPending = false;
      if (this.active.generating) return;
      this.active = null;
      finishedSpeaking = true;
    }
    this.mic.setPlaybackBias(false, this.settings);
    this.echoGuard.reset();
    if (!this.assistantActive()) {
      useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
      // Natural end of assistant audio (not interrupt/stop). Skip if the user already took the floor.
      if (finishedSpeaking && !this.pending && !this.userSpeaking && this.sttInflight === 0) {
        this.playTurnCue();
      }
    }
  }

  /** Soft boundary chime; briefly raises VAD thresholds so the speakers don't re-trigger listening. */
  private playTurnCue() {
    this.playCueWithBias('turn');
  }

  /** Descending barge-in earcon at the moment of hard stop. */
  private playInterruptCue() {
    this.playCueWithBias('interrupt');
  }

  private playCueWithBias(kind: 'turn' | 'interrupt') {
    if (this.settings.mode === 'text') return;
    const ms = this.player.playCue(kind);
    this.mic.setPlaybackBias(true, this.settings);
    if (this.cueBiasTimer) window.clearTimeout(this.cueBiasTimer);
    this.cueBiasTimer = window.setTimeout(() => {
      this.cueBiasTimer = null;
      // Don't drop bias if the assistant has started speaking again.
      if (!this.assistantAudible()) this.mic.setPlaybackBias(false, this.settings);
    }, ms + 40);
  }

  // ---- turns -----------------------------------------------------------------

  async sendText(text: string) {
    if (!text.trim()) return;
    if (this.assistantActive()) await this.stopAssistant('lexical-interrupt');
    if (this.interrupt) {
      this.clearInterrupt();
      useStore.getState().setPendingUtterance('');
    }
    await this.startTurn(text.trim(), { turnCommitted: Date.now() }, this.typedWantAudio());
  }

  /** Typed turns only request TTS once the audio pipeline has been started by a user gesture. */
  private typedWantAudio(): boolean {
    return typedTurnWantsAudio({ ttsEnabled: this.settings.ttsEnabled, mode: this.settings.mode, audioStarted: this.started });
  }

  /** Create a sibling assistant reply under the last user on the active path. */
  async regenerate() {
    if (this.assistantActive()) await this.stopAssistant('lexical-interrupt');
    const wantAudio = this.typedWantAudio();
    useStore.getState().setArbiterState('thinking');
    try {
      const r = await ws.rpc<{ turnId: string; userText: string }>('turn.regenerate', { wantAudio }, 120_000);
      this.active = { turnId: r.turnId, generating: true, audioPending: false, audioStarted: false };
      this.lastCommit = null;
      this.arbiter.resetForTurn(r.turnId);
      useStore.getState().setStreaming({ turnId: r.turnId, text: '' });
    } catch (err) {
      useStore.getState().log('error', `regenerate failed: ${err instanceof Error ? err.message : err}`);
      this.active = null;
      useStore.getState().setStreaming(null);
      useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
    }
  }

  /** Step back one user↔assistant turn on the active branch. */
  async undoTurn() {
    if (this.assistantActive()) await this.stopAssistant('lexical-interrupt');
    try {
      await ws.rpc('history.undo');
      this.active = null;
      this.clearStreamPending();
      useStore.getState().setStreaming(null);
      useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
    } catch (err) {
      useStore.getState().log('error', `undo failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  /** Restore the leaf saved by the last Undo (if any). */
  async forwardTurn() {
    if (this.assistantActive()) await this.stopAssistant('lexical-interrupt');
    try {
      await ws.rpc('history.forward');
      this.active = null;
      this.clearStreamPending();
      useStore.getState().setStreaming(null);
      useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
    } catch (err) {
      useStore.getState().log('error', `forward failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  /** Switch to a sibling branch at `messageId`. */
  async selectBranch(messageId: string) {
    if (this.assistantActive()) await this.stopAssistant('lexical-interrupt');
    try {
      await ws.rpc('history.selectBranch', { messageId });
      this.active = null;
      this.clearStreamPending();
      useStore.getState().setStreaming(null);
      useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
    } catch (err) {
      useStore.getState().log('error', `branch switch failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  /** Start a fresh conversation, optionally keeping long-term memory/persona. */
  async newConversation(opts: { preserveMemories?: boolean; name?: string; caseId?: string | null } = {}) {
    if (this.assistantActive()) await this.stopAssistant('lexical-interrupt');
    this.player.stop();
    this.active = null;
    this.clearStreamPending();
    useStore.getState().setStreaming(null);
    try {
      const activeCaseId = opts.caseId !== undefined ? opts.caseId : useStore.getState().activeCaseId;
      await ws.rpc('session.create', {
        name: opts.name,
        preserveMemories: !!opts.preserveMemories,
        preservePinned: !!opts.preserveMemories,
        preserveSystemPrompt: true,
        preserveSummary: false,
        caseId: activeCaseId,
      });
      useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
      useStore.getState().log('info', opts.preserveMemories ? 'New conversation (memories preserved)' : 'New conversation');
    } catch (err) {
      useStore.getState().log('error', `new conversation failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  async switchCase(caseId: string) {
    if (this.assistantActive()) await this.stopAssistant('lexical-interrupt');
    this.player.stop();
    this.active = null;
    this.clearStreamPending();
    useStore.getState().setStreaming(null);
    try {
      await ws.rpc('case.switch', { caseId });
      useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
      useStore.getState().log('info', 'Switched case');
    } catch (err) {
      useStore.getState().log('error', `case switch failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  async switchConversation(id: string) {
    if (this.assistantActive()) await this.stopAssistant('lexical-interrupt');
    this.player.stop();
    this.active = null;
    this.clearStreamPending();
    useStore.getState().setStreaming(null);
    try {
      await ws.rpc('session.switch', { id });
      useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
    } catch (err) {
      useStore.getState().log('error', `switch conversation failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  private async startTurn(text: string, marks: Partial<Record<TurnMark, number>>, wantAudio: boolean, replaceUserMessageId?: string) {
    // A reply still being generated (no audio yet) is superseded by the new user turn.
    if (this.active && !this.assistantAudible()) {
      await this.stopAssistant('lexical-interrupt');
    }
    const turnId = newId('t');
    marks.turnCommitted = marks.turnCommitted ?? Date.now();
    this.active = { turnId, generating: true, audioPending: false, audioStarted: false };
    // Voice turns can be recalled if the user keeps talking; typed turns cannot.
    this.lastCommit = marks.speechStart !== undefined ? { turnId, text, marks: { ...marks }, ts: Date.now() } : null;
    this.arbiter.resetForTurn(turnId);
    this.clearStreamPending();
    useStore.getState().setStreaming({ turnId, text: '' });
    useStore.getState().setArbiterState('thinking');
    try {
      await ws.rpc('turn.start', { turnId, text, marks, wantAudio, replaceUserMessageId });
    } catch (err) {
      useStore.getState().log('error', `turn.start failed: ${err instanceof Error ? err.message : err}`);
      if (this.active?.turnId === turnId) this.active = null;
      if (this.lastCommit?.turnId === turnId) this.lastCommit = null;
      this.clearStreamPending();
      useStore.getState().setStreaming(null);
      useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
    }
  }

  /** Stop button / new input while the assistant is active. Awaits server interrupt for ordered supersede. */
  async stopAssistant(reason: BargeinTier): Promise<void> {
    const turnId = this.active?.turnId ?? this.player.activeTurn();
    const heard = this.player.heardText();
    // Mark dead before stop so any in-flight turn.audio is rejected at the gate.
    if (turnId) this.markDead(turnId);
    this.player.stop();
    this.arbiter.cancel({ resume: false });
    if (this.interrupt) useStore.getState().setPendingUtterance('');
    this.clearInterrupt();
    // player.stop() skips onIdle (sources marked ended before stop), so clear bias here.
    this.clearPlaybackGate();
    this.active = null;
    this.lastCommit = null;
    this.clearStreamPending();
    useStore.getState().setStreaming(null);
    useStore.getState().setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
    if (turnId) {
      try {
        await ws.rpc('turn.interrupt', { turnId, heardText: heard, reason }, ConversationController.INTERRUPT_RPC_TIMEOUT_MS);
      } catch {
        /* client already stopped locally; server may already be idle or slow */
      }
    }
  }

  /** After TTS is paused/stopped: normal VAD thresholds and a fresh echo baseline. */
  private clearPlaybackGate() {
    this.mic.setPlaybackBias(false, this.settings);
    this.echoGuard.reset();
  }

  private markDead(turnId: string) {
    this.deadTurns.add(turnId);
    if (this.deadTurns.size > 50) this.deadTurns.delete(this.deadTurns.values().next().value!);
  }

  private hardInterrupt(turnId: string, tier: BargeinTier, transcript: string | null, marks: InterruptMarks) {
    void this.runInterruptStop({ turnId, tier, transcript, marks, heard: this.player.heardText() });
  }

  private softYield(turnId: string, tier: BargeinTier, transcript: string, marks: InterruptMarks) {
    void this.runInterruptStop({ turnId, tier, transcript, marks, heard: this.player.heardThroughCurrentChunk() });
  }

  /**
   * Hard-stop assistant audio for barge-in.
   * With a seed transcript: open interrupt capture (user may still be talking).
   * With no seed: only stop — arbiter hands audio to main-lane STT (avoids energy-capture limbo).
   */
  private async runInterruptStop(p: { turnId: string; tier: BargeinTier; transcript: string | null; marks: InterruptMarks; heard: string }) {
    const seed = (p.transcript ?? '').trim();
    // Claim capture BEFORE any await when we have text; empty seed must not open capture.
    if (seed) this.beginInterruptUtterance(seed, p.marks.speechStart, p.marks.cleanFrom);
    this.markDead(p.turnId);
    this.player.stop();
    // Keep a duration-gate candidate so the upcoming VAD speechEnd still hands trimmed audio over.
    this.arbiter.cancel({ resume: false, keepHardInterrupted: true });
    this.playInterruptCue();
    this.clearPlaybackGate();
    if (!seed) {
      // No capture opened: the UI must not stay on paused/ducked. The segment's main-lane STT
      // (or the state watchdog) moves us to thinking/listening from here.
      const pu = useStore.getState().pendingUtterance;
      if (pu === '…listening') useStore.getState().setPendingUtterance('');
      useStore.getState().setArbiterState('listening');
    }
    try {
      await ws.rpc('turn.interrupt', { turnId: p.turnId, heardText: p.heard, reason: p.tier });
    } catch {
      /* client still owns the local stop; server may already be idle */
    }
    this.active = null;
    this.lastCommit = null;
    useStore.getState().setStreaming(null);
    useStore.getState().log('info', `[arbiter] interrupt (${p.tier}); heard: "${p.heard.slice(-60)}"`);
  }

  private addAside(turnId: string, transcript: string, outcome: BargeinOutcome, currentSentence: string) {
    void ws.rpc('aside', { turnId, text: transcript, outcome, currentSentence }).catch(() => {});
  }

  private recordEvent(ev: ArbiterEvent) {
    useStore.getState().upsertArbiterEvent(ev);
    void ws.rpc<ArbiterEvent>('arbiter.event', ev).then((saved) => useStore.getState().upsertArbiterEvent(saved)).catch(() => {});
  }

  private async stt(
    audio: Float32Array,
    lane: 'main' | 'bargein',
    prompt?: string,
    partial = false,
  ): Promise<SttResult> {
    // Whisper prompt: the utterance so far, clipped to the last ~200 chars (Whisper only reads the tail).
    const p = prompt?.trim() ? prompt.trim().slice(-200) : undefined;
    return ws.sttBinary(audio, { lane, sampleRate: 16000, prompt: p, turnId: this.active?.turnId, partial }, 60_000);
  }

  // ---- server events -----------------------------------------------------------

  private onServerEvent(ev: ServerEvent) {
    const st = useStore.getState();
    switch (ev.type) {
      case 'turn.token':
        if (this.active?.turnId === ev.data.turnId) {
          this.streamPending = { turnId: ev.data.turnId, text: ev.data.text };
          if (this.streamRaf === null) {
            this.streamRaf = requestAnimationFrame(() => {
              this.streamRaf = null;
              const pending = this.streamPending;
              if (pending && this.active?.turnId === pending.turnId) {
                useStore.getState().setStreaming(pending);
              }
            });
          }
        }
        break;
      case 'turn.audio':
        if (this.deadTurns.has(ev.data.turnId)) return;
        // After interrupt active is null — do not require active !== null (that failed open).
        if (
          this.active?.turnId !== ev.data.turnId &&
          this.player.activeTurn() !== ev.data.turnId &&
          !this.player.isQueueing(ev.data.turnId)
        ) {
          return;
        }
        if (this.active?.turnId === ev.data.turnId) this.active.audioPending = true;
        this.audioInflight++;
        {
          const wav = ev.data.wavBytes ?? ev.data.wavBase64;
          if (!wav) break;
          void this.player
            .enqueue(ev.data.turnId, ev.data.index, ev.data.text, wav, {
              format: ev.data.format,
              sampleRate: ev.data.sampleRate,
            })
            .catch((err) => st.log('warn', `audio decode failed: ${err}`))
            .finally(() => {
              this.audioInflight = Math.max(0, this.audioInflight - 1);
              if (this.audioInflight === 0 && !this.player.isPlaying()) this.onPlayerIdle(ev.data.turnId);
            });
        }
        break;
      case 'turn.done':
        if (this.lastCommit?.turnId === ev.data.turnId && !ev.data.interrupted && !this.active?.audioPending && !this.player.isPlaying()) {
          // Reply finished without audio (text mode): nothing to recall any more.
          this.lastCommit = null;
        }
        if (this.active?.turnId === ev.data.turnId) {
          this.active.generating = false;
          // A queue that never produced a chunk start by turn.done is dead (suspended context or
          // dropped chunks): stop it rather than waiting on isPlaying forever.
          const deadAudio = !this.active.audioStarted && this.audioInflight === 0;
          if (deadAudio && this.player.isPlaying()) this.player.stop();
          if (deadAudio || (!this.player.isPlaying() && this.audioInflight === 0)) {
            this.active = null;
            st.setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
          }
        }
        if (this.streamPending?.turnId === ev.data.turnId) this.clearStreamPending();
        // A recalled/superseded turn finishing late must not wipe the newer turn's stream.
        if (!st.streaming || st.streaming.turnId === ev.data.turnId) st.setStreaming(null);
        break;
      case 'turn.error': {
        const matchesActive = this.active?.turnId === ev.data.turnId;
        const matchesStream = st.streaming?.turnId === ev.data.turnId || this.streamPending?.turnId === ev.data.turnId;
        if (!matchesActive && !matchesStream) break;
        if (matchesActive) this.active = null;
        if (this.streamPending?.turnId === ev.data.turnId) this.clearStreamPending();
        if (!st.streaming || st.streaming.turnId === ev.data.turnId) st.setStreaming(null);
        st.log('error', ev.data.error);
        st.setArbiterState(this.settings.mode === 'text' ? 'idle' : 'listening');
        break;
      }
      default:
        break;
    }
  }
}

export const controller = new ConversationController();
